<template>
  <v-container class="pl-0">
    <v-card-title class="pa-1">
      <h3 :class="fieldError ? 'red--text wiggle' : 'white--text'">{{ field.label }}</h3>
    </v-card-title>
    <v-card-subtitle v-if="field.helpText" class="pa-1">
      <p class="mt-1 grey--text text--lighten-1 text-caption font-weight-regular">{{ field.helpText }}</p>
    </v-card-subtitle>
    <v-col v-if="!field.multivalued && newValue" :cols="fullForm ? 3 : 5" class="pl-0">
      <CloudinaryItem
        v-if="newValue.url"
        :value="newValue"
        :refresh-loading="loading"
        :cloudinary-loading="cloudinaryLoading"
        :field="field"
        @get-cloudinary-resource="getCloudinarySingleFieldResource()"
        @launch-cloudinary="launchCloudinary()"
        @delete-resource="deleteSingleFieldResource()"
      />
    </v-col>
    <draggable
      v-else
      v-model="newValue"
      :disabled="!store.getters.permissionToWrite || loading.length > 0 || cloudinaryLoading || (newValue && newValue.length <= 1)"
      class="d-flex flex-wrap"
      handle=".item"
      style="width: 100%"
    >
      <v-col v-for="(resource, resourceIndex) in newValue" :key="resourceIndex" :cols="fullForm ? 3 : 5" class="item">
        <CloudinaryItem
          v-if="resource?.url"
          :value="resource"
          :refresh-loading="loading.includes(resourceIndex)"
          :items-loading="loading.length > 0"
          :cloudinary-loading="cloudinaryLoading"
          :field="field"
          :class="{ 'move-cursor': newValue.length > 1 && loading.length <= 0 && !cloudinaryLoading }"
          @get-cloudinary-resource="getCloudinaryMultiFieldResource(resourceIndex)"
          @delete-resource="deleteMultiFieldResource(resourceIndex)"
        />
      </v-col>
    </draggable>
    <div v-if="store.getters.permissionToWrite && (field.multivalued || (!field.multivalued && !newValue?.url))">
      <v-btn v-if="!cloudinaryLoading" icon @click="launchCloudinary()">
        <v-icon :color="fieldError ? 'red' : ''">add_photo_alternate</v-icon>
      </v-btn>
      <v-progress-circular v-else class="loading" :width="3" :size="25" indeterminate color="info" />
    </div>

    <v-card-subtitle v-if="fieldError" class="px-0 py-0">
      <span class="red--text">{{ fieldError }}</span>
    </v-card-subtitle>

    <v-text-field v-show="false" ref="cloudinaryRules" v-model="newValue" :rules="field.rules" readonly />
  </v-container>
</template>

<script setup>
import { useStore } from 'vue2-helpers/vuex';
import { useApolloClient } from '@vue/apollo-composable';

import { ref, computed, watch } from 'vue';
import gql from 'graphql-tag';

import draggable from 'vuedraggable';

import CloudinaryItem from './CloudinaryItem.vue';

// eslint-disable-next-line no-undef
const emit = defineEmits(['input']);

// eslint-disable-next-line no-undef
const props = defineProps({
  field: { type: Object, default: () => { } },
  value: { type: [Array, Object, String], default: null },
  fullForm: { type: Boolean, default: false } // used for vuetify col size
});

/*
  This component only works with ImageCloudinary types.
  - normalize: if the props.value has another type then ImageCloudinary (it needs a normalization)
   ex: String OR [ String ] -to-> { url: String } OR [{ url: String }]
  - denormalize: if the reference of props.value has been trigger  (it needs a denormalization)
   ex: { url: String } OR [{ url: String }] -to-> String OR [ String ]
*/
const imageCloudinaryType = {
  normalize: fieldData => {
    if (!fieldData || (Array.isArray(fieldData) && !fieldData.length)) return props.field.multivalued ? [] : null;

    if (Array.isArray(fieldData)) {
      return fieldData.map(v => ({ url: v }));
    }

    return { url: fieldData };
  },
  denormalize: fieldData => {
    if (!fieldData || (Array.isArray(fieldData) && !fieldData.length)) return null;

    if (Array.isArray(fieldData)) {
      return fieldData.map(v => v.url);
    }

    return fieldData.url;
  }
};

const store = useStore();

// Only normalize to ImageCloudinaryType, if props.value is null or not from type ImageCloudinary
const imageCloudinary = (!props.value ? imageCloudinaryType.normalize(props.value) : props.field.type === 'ImageCloudinary' ? props.value : imageCloudinaryType.normalize(props.value));
const newValue = ref(imageCloudinary);

const loading = ref(props.field.multivalued ? [] : false);
const cloudinaryLoading = ref(false);
const changed = ref(false);

const cloudinaryRules = ref(null);
const fieldError = computed(() => {
  if (!changed.value) return null;
  if (cloudinaryRules.value && cloudinaryRules.value.valid) return null;
  return (cloudinaryRules.value && cloudinaryRules.value.errorBucket.length) ? cloudinaryRules.value.errorBucket[0] : null;
});

const { resolveClient } = useApolloClient();

// This request is for field.metadata to reset cache
const getCloudinaryResource = async url => {
  const apollo = resolveClient();

  return apollo.query({
    query: gql(`
      query ${store.state.environment.PROJECT}CloudinaryResource($url: String, $resetCache: Boolean ) { 
        ${store.state.environment.PROJECT}CloudinaryResource(url: $url, resetCache: $resetCache) 
      }
    `),
    variables: { url: url, resetCache: true },
    fetchPolicy: 'no-cache'
  }).then(res => res.data).catch(error => {
    console.log('Error (getCloudinaryResource): ', error);
  });
};

const getCloudinarySingleFieldResource = async () => {
  loading.value = true;

  const data = await getCloudinaryResource(newValue.value.url);
  if (data && data.labalaguereCloudinaryResource) {
    newValue.value.metadata = { ...data.labalaguereCloudinaryResource };
  } else {
    newValue.value.metadata = null;
  }
  loading.value = false;
};

const getCloudinaryMultiFieldResource = async resourceKey => {
  loading.value.push(resourceKey);

  const data = await getCloudinaryResource(newValue.value[resourceKey].url);

  const labalaguerImageAux = newValue.value[resourceKey];
  if (data && data.labalaguereCloudinaryResource) {
    labalaguerImageAux.metadata = { ...data.labalaguereCloudinaryResource };
  } else {
    labalaguerImageAux.metadata = null;
  }

  newValue.value.splice(resourceKey, 1, labalaguerImageAux);

  const loadingIndex = loading.value.findIndex(lIndex => lIndex === resourceKey);
  if (loadingIndex != -1) {
    loading.value.splice(loadingIndex, 1);
  }
};

const launchCloudinary = () => {
  let hasAssets = false; // for cloudinary hideHandler, ex. When closing iframe without any change
  cloudinaryLoading.value = true;

  const config = { ...store.state.cloudinaryConfig, multiple: props.field?.multivalued || false };

  if (store?.state?.workspace?.settings?.cloudinaryFolder) {
    config.folder = { path: store.state.workspace.settings.cloudinaryFolder };
  }

  // eslint-disable-next-line no-undef
  cloudinary.openMediaLibrary(
    config,
    {
      insertHandler: data => {
        hasAssets = true;

        for (const asset of data.assets) {
          // Gets the path and name of the resource (ex: /folderA/folderB/name.jpeg)
          const { public_id } = asset;

          // Gets the derived transformation in case it has one
          let derivation = '';
          if (asset.derived) {
            derivation = `/${asset.derived[0].raw_transformation}`;
          }

          // Gets extension of the file
          const extension = asset.format;

          // Generates the url
          const url = `${derivation}/${public_id}.${extension}`;

          // Gets the metadata
          const metadataAux = {};
          if (props.field.metadata) {
            if (asset?.metadata?.tfdk8z8bu0vwpg6bwtd9) {
              metadataAux.credit = asset.metadata.tfdk8z8bu0vwpg6bwtd9;
            }
            if (asset?.context?.custom?.caption) {
              metadataAux.title = asset.context.custom.caption;
            }
            if (asset?.context?.custom?.alt) {
              metadataAux.alt = asset.context.custom.alt;
            }
          }

          // Check if it is multivalued
          if (props.field.multivalued) {
            if (!newValue.value) {
              newValue.value = [];
            }
            newValue.value.push({
              url: url,
              caption: null,
              metadata: metadataAux
            });
          } else {
            newValue.value = {
              url: url,
              caption: newValue.value?.caption,
              metadata: metadataAux
            };
          }
        }

        cloudinaryLoading.value = false;
      },
      hideHandler: () => {
        if (!hasAssets) {
          cloudinaryLoading.value = false;
        }
      }
    }
  );
};

const deleteSingleFieldResource = () => {
  newValue.value = null;
};

const deleteMultiFieldResource = index => {
  newValue.value.splice(index, 1);
};

// Watching complex ref data types: https://www.netlify.com/blog/2021/01/29/deep-dive-into-the-vue-composition-apis-watch-method/#complex
const watchValue = (props.field.multivalued ? () => [...newValue.value] : newValue);
watch(watchValue, value => {
  changed.value = true;
  const data = props.field.type === 'ImageCloudinary' ? value : imageCloudinaryType.denormalize(value);

  emit('input', data);
});

</script>
